import type { MergeTbaIntoBookRequest, MergeTbaIntoBookResponse } from "@book-club/shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { booksKeys } from "@/features/books/queries/booksQueryKeys";
import booksService from "@/features/books/services/booksService";
import { sessionsKeys } from "@/features/sessions/queries/sessionsQueryKeys";
import { votingQueryKeys } from "@/features/voting/queries/votingQueryKeys";

export function useMergeTbaIntoBook() {
    const queryClient = useQueryClient();

    const mutation = useMutation<MergeTbaIntoBookResponse, Error, MergeTbaIntoBookRequest>({
        mutationFn: async (input) => {
            return await booksService.mergeTbaIntoBook(input);
        },
        onError: (error) => {
            const errorMessage = error instanceof Error ? error.message : "Failed to merge TBA into book";
            toast.error(errorMessage);
        },
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries({ queryKey: booksKeys.all });
            queryClient.invalidateQueries({ queryKey: booksKeys.detail(variables.tbaBookId) });
            queryClient.invalidateQueries({ queryKey: booksKeys.detail(variables.targetBookId) });
            queryClient.invalidateQueries({ queryKey: sessionsKeys.all });
            if (variables.votingPeriodId) {
                queryClient.invalidateQueries({ queryKey: votingQueryKeys.periods() });
            }
            toast.success(
                data.alreadyMerged ? "Book was already merged from this TBA" : "TBA merged into book successfully"
            );
        },
    });

    return {
        error: mutation.error,
        isError: mutation.isError,
        isPending: mutation.isPending,
        mutate: mutation.mutate,
        mutateAsync: mutation.mutateAsync,
    };
}
